import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { AnimatePresence, LazyMotion, m } from 'framer-motion';
import { LandingHeroSlide } from '@components/Landing/sections/Hero/Slide';
import { BackgroundBlur } from '@components/basics/BackgroundBlur';
import { GridCarousel } from '@components/main-components/carousel/GridCarousel';
import { NextFutureImage } from '@components/main-components/image/NextFutureImage';
import { MediaQuery } from '@context/mediaQuery';
import type { HeroHighlightedReleasesQuery } from '@graphql/reactQuery';
import { getFragment, gql, LandingHeroBackgroundFragmentDoc } from '@graphql/reactQuery';
import { useIsSsr } from '@hooks/useIsSsr';
import { useAudioController } from '@store/audio/AudioController';
import { getNextTrackIds } from '@utils/helpers';

const loadDomMaxFeatures = () => import('@utils/loadFramerFeatures').then(res => res.domMax);

export type HighlightedReleases = HeroHighlightedReleasesQuery['highlightedReleases'];

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment LandingHeroBackground on Release {
    id
    coverImage {
      id
      url
    }
    staticCoverImage {
      id
      url
    }
    track {
      id
    }
  }
`);

const FEATURED_RELEASE_MOBILE_MAX = 5;
const MARGIN_BETWEEN_SLIDES_MOBILE = 8;
const MARGIN_BETWEEN_SLIDES_TABLET = 12;
const SLIDE_TIMING = 5000;

export const Hero = ({ highlightedReleases }: { highlightedReleases: HighlightedReleases }) => {
  const [releaseIndex, setReleaseIndex] = useState<number>(0);
  const [isHovering, setIsHovering] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);
  const release = getFragment(LandingHeroBackgroundFragmentDoc, highlightedReleases[releaseIndex]);
  const backgroundImageUrl = release?.staticCoverImage?.url ?? release?.coverImage?.url ?? '';
  const maxReleases = Math.min(highlightedReleases.length, FEATURED_RELEASE_MOBILE_MAX);

  const isSsr = useIsSsr();
  const { tablet, desktop } = MediaQuery.useContainer();
  const { playing, activeTrackId } = useAudioController();
  const isPlayingHeroTrack = release?.track.id === activeTrackId && playing;
  const isPaused = !tablet || isHovering || isPlayingHeroTrack;
  const timeoutId = useRef<ReturnType<typeof setTimeout>>();

  const onCurrentIndexChange = useCallback((i: number) => {
    setReleaseIndex(i);
  }, []);

  const slides = useMemo(
    () =>
      highlightedReleases.slice(0, FEATURED_RELEASE_MOBILE_MAX).map((release, index) => ({
        id: release.id,
        slide: release ? (
          <div key={release.id} className="mr-2 w-full flex-shrink-0 md:mr-3">
            <LandingHeroSlide
              index={index}
              release={release}
              setIsHovering={setIsHovering}
              getNextTrackIds={getNextTrackIds({
                trackId: release.track.id,
                releases: highlightedReleases.slice(0, FEATURED_RELEASE_MOBILE_MAX),
              })}
            />
          </div>
        ) : null,
      })),
    [highlightedReleases, setIsHovering],
  );

  useEffect(() => {
    if (isPaused) return;

    timeoutId.current = setTimeout(() => {
      setReleaseIndex((prev: number) => (prev < maxReleases - 1 ? prev + 1 : 0));
    }, SLIDE_TIMING);

    return () => clearTimeout(timeoutId.current);
  }, [releaseIndex, maxReleases, isPaused, setReleaseIndex]);

  return (
    <div className="relative flex h-[552px] w-full flex-col md:h-[416px] lt:h-[536px]">
      {/* Blurred wrapper background */}
      <LazyMotion features={loadDomMaxFeatures}>
        <AnimatePresence initial={false}>
          <m.div
            key={backgroundImageUrl}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
          >
            <NextFutureImage
              className="object-cover object-center"
              fill
              src={backgroundImageUrl}
              quality={1}
              alt=""
              role="presentation"
            />
          </m.div>
        </AnimatePresence>
      </LazyMotion>
      <BackgroundBlur />
      <div className="absolute bottom-0 left-0 h-[295px] w-full bg-gradient-to-t from-white md:bottom-0 md:h-[215px] lt:h-[289px]" />

      {/* Slide progress indicators */}
      <div
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        className="absolute top-[88px] z-header flex w-full justify-center gap-2 md:top-[368px] md:z-above1 md:pr-6 lt:top-[538px]"
      >
        {Array.from({
          length: maxReleases,
        }).map((_, index) => (
          <button
            key={index}
            className="group flex h-4 items-center justify-center md:h-7"
            onClick={() => setReleaseIndex(index)}
          >
            <span
              className={clsx(
                'relative h-1 w-9 transform-gpu overflow-hidden rounded-lg bg-white/20 after:absolute after:left-0 after:top-0 after:h-full after:bg-white after:content-[""] lt:w-16',
                index === releaseIndex && !isSsr
                  ? isPaused
                    ? 'after:w-full after:animate-[fadeIn_0.3s_linear]'
                    : 'after:animate-[expand_5s_linear_forwards]'
                  : 'after:w-0 group-hover:after:w-full group-hover:after:opacity-30',
                'md:bg-black/20 md:after:bg-black',
              )}
            />
          </button>
        ))}
      </div>

      {/* Slides */}
      <div
        className="absolute bottom-0 left-0 mt-6 w-full max-w-[1280px] px-4 md:px-6 2xl:left-[calc(50%-640px)]"
        ref={wrapper}
      >
        <GridCarousel
          slides={slides}
          columnAmount={1}
          hideArrows
          onInternalActiveIndexChange={onCurrentIndexChange}
          externalActiveIndex={releaseIndex}
          spacing={
            tablet ? (desktop ? null : MARGIN_BETWEEN_SLIDES_TABLET) : MARGIN_BETWEEN_SLIDES_MOBILE
          }
          ssrSlideWidth="100%"
          onEndReached={null}
          alignSlidesLeft={false}
          columnType="default"
        />
      </div>
    </div>
  );
};
